import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';

interface HackerRankTemplate {
  id: number | string;
  name: string;
}

interface FetchResult {
  templates: HackerRankTemplate[];
  /** True when the BE returned `error: 'hackerrank_account_not_connected'`
   *  (HTTP 422) — the workspace has the HR integration but the current
   *  user doesn't have a personal HR for Work account. Legacy uses this
   *  to disable the "HackerRank" location option with a hover tooltip. */
  userHasNoAccount: boolean;
}

async function fetchHackerRankTemplates(): Promise<FetchResult> {
  const res = (await http.get('/hacker_rank/list_interview_templates')) as
    | {
        data?: HackerRankTemplate[];
        error?: string;
        status?: number;
        details?: string;
      }
    | undefined;
  if (Array.isArray(res?.data)) {
    return { templates: res.data, userHasNoAccount: false };
  }
  // 422 with `error: hackerrank_account_not_connected` lands here as a
  // resolved (non-thrown) body because the http interceptor doesn't
  // throw on this status. Capture the signal so callers can disable
  // the HR location option without re-firing the request.
  return {
    templates: [],
    userHasNoAccount: res?.error === 'hackerrank_account_not_connected',
  };
}

export function useHackerRankTemplates(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const { data, isLoading } = useQuery({
    queryKey: ['hackerrank', 'interview-templates'],
    queryFn: fetchHackerRankTemplates,
    staleTime: 0,
    enabled,
  });
  const userHasNoAccount = data?.userHasNoAccount ?? false;
  // Stable reference so callers' useMemos don't churn when `data` is
  // briefly undefined (loading state). Without this, `templates` is a
  // fresh `[]` on every render and any downstream memo keyed on it
  // re-runs unnecessarily.
  const templates = useMemo(() => data?.templates ?? [], [data?.templates]);

  const templateOptions = useMemo(
    () =>
      templates.length > 0
        ? templates.map((t) => ({
            value: String(t.id),
            label: t.name,
          }))
        : // Mirror legacy `useSchLocationField` empty-state — without it the
          // dropdown silently renders blank and looks like the integration
          // is broken. The empty-string value + `disabled: true` keeps the
          // option unselectable while still communicating the state.
          [
            {
              value: '',
              label: 'No Template Available',
              disabled: true,
            },
          ],
    [templates]
  );

  return { templates, templateOptions, isLoading, userHasNoAccount };
}
